import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useSettings } from '../../context/SettingsContext';
import {
  FileText, RefreshCw, Search, ChevronDown, ChevronUp,
  TrendingDown, CheckCircle2, Package, IndianRupee,
  Users, X, Receipt, AlertCircle
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

function safeDate(d) {
  if (!d) return '—';
  try { const p = parseISO(d); return isValid(p) ? format(p, 'd MMM yyyy') : '—'; } catch { return '—'; }
}

const STATUS_CFG = {
  pending:         { label: 'Pending',         bg: 'bg-gray-100',    text: 'text-gray-600'   },
  acknowledged:    { label: 'Acknowledged',    bg: 'bg-blue-100',    text: 'text-blue-700'   },
  processing:      { label: 'Processing',      bg: 'bg-amber-100',   text: 'text-amber-700'  },
  payment_pending: { label: 'Pymnt Pending',   bg: 'bg-orange-100',  text: 'text-orange-700' },
  advance_pending: { label: 'Adv Pending',     bg: 'bg-yellow-100',  text: 'text-yellow-700' },
  completed:       { label: 'Completed',       bg: 'bg-green-100',   text: 'text-green-700'  },
  cancelled:       { label: 'Cancelled',       bg: 'bg-red-100',     text: 'text-red-600'    },
};

function Bar({ pct, color = 'amber' }) {
  const colors = { amber: 'bg-amber-500', green: 'bg-green-500', blue: 'bg-blue-500', red: 'bg-red-400', purple: 'bg-purple-500' };
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1.5">
      <div className={`h-1.5 rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : colors[color]}`} style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
  );
}

function PORow({ po, expanded, onToggle }) {
  const { formatCurrency } = useSettings();
  const st = STATUS_CFG[po.status] || { label: po.status, bg: 'bg-gray-100', text: 'text-gray-600' };

  const hasSales    = po.sale_count > 0;
  const fullyBilled = po.sale_pct >= 100;
  const fullyPaid   = po.paid_pct >= 100;
  const hasExcess   = po.excess_payment > 0;

  return (
    <div className={`border-2 rounded-2xl overflow-hidden transition-all ${expanded ? 'border-amber-300 shadow-lg' : 'border-gray-200 hover:border-amber-200 hover:shadow-sm'} bg-white`}>

      {/* ── Header ── */}
      <div className="flex items-start gap-3 p-4 cursor-pointer" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-900">{po.po_number}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
            {hasExcess    && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Overpaid</span>}
            {fullyBilled  && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Fully Billed</span>}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">👤 {po.client_name} · {safeDate(po.order_date)}</p>
          {po.item_name && <p className="text-xs text-gray-400 mt-0.5 truncate">📦 {po.item_name}</p>}
        </div>

        {/* Quick numbers — desktop */}
        <div className="hidden lg:grid grid-cols-3 gap-4 text-right flex-shrink-0 mr-2">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">PO Value</p>
            <p className="text-sm font-bold text-gray-900">{formatCurrency(po.po_amount)}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Invoiced</p>
            <p className={`text-sm font-bold ${fullyBilled ? 'text-green-600' : hasSales ? 'text-amber-600' : 'text-gray-300'}`}>
              {formatCurrency(po.sale_amount)}
              <span className="text-xs font-normal ml-1 text-gray-400">({po.sale_pct}%)</span>
            </p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Received</p>
            <p className={`text-sm font-bold ${fullyPaid ? 'text-green-600' : po.paid_amount > 0 ? 'text-purple-600' : 'text-gray-300'}`}>
              {formatCurrency(po.paid_amount)}
              <span className="text-xs font-normal ml-1 text-gray-400">({po.paid_pct}%)</span>
            </p>
          </div>
        </div>

        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />}
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/40 p-4 space-y-4">

          {/* 3 summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

            {/* Quantity */}
            {po.po_qty > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" /> Quantity
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ordered</span>
                    <span className="font-bold text-gray-900">{po.po_qty} pcs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Invoiced ({po.sale_count} sale{po.sale_count !== 1 ? 's' : ''})</span>
                    <span className="font-semibold text-amber-600">{po.sold_qty} pcs</span>
                  </div>
                  <div className="border-t border-dashed border-gray-100 pt-2 flex justify-between">
                    <span className="text-gray-500">Remaining</span>
                    <span className={`font-bold ${(po.remaining_qty||0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {po.remaining_qty ?? 0} pcs
                    </span>
                  </div>
                </div>
                <Bar pct={po.po_qty > 0 ? Math.round(po.sold_qty / po.po_qty * 100) : 0} color="amber" />
                <p className="text-[10px] text-gray-400 mt-1 text-right">{Math.min(Math.round(po.sold_qty/po.po_qty*100),100)}% invoiced</p>
              </div>
            )}

            {/* Sale Billing */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" /> Sale Invoices
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">PO Value</span>
                  <span className="font-bold text-gray-900">{formatCurrency(po.po_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoiced ({po.sale_count})</span>
                  <span className="font-semibold text-amber-600">{formatCurrency(po.sale_amount)}</span>
                </div>
                <div className="border-t border-dashed border-gray-100 pt-2 flex justify-between">
                  <span className="text-gray-500">Remaining</span>
                  <span className={`font-bold ${po.remaining_amount > 0 ? 'text-orange-600' : po.remaining_amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {po.remaining_amount <= 0 ? 'Fully billed' : formatCurrency(po.remaining_amount)}
                    {po.remaining_amount < 0 && <span className="text-xs font-normal ml-1">(excess)</span>}
                  </span>
                </div>
              </div>
              <Bar pct={po.sale_pct} color="amber" />
              <p className="text-[10px] text-gray-400 mt-1 text-right">{po.sale_pct}% billed</p>
            </div>

            {/* Payment */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <IndianRupee className="w-3.5 h-3.5" /> Payment
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Received</span>
                  <span className="font-bold text-gray-900">{formatCurrency(po.paid_amount)}</span>
                </div>
                {po.advance_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">of which Advance</span>
                    <span className="font-semibold text-purple-600">{formatCurrency(po.advance_amount)}</span>
                  </div>
                )}
                <div className="border-t border-dashed border-gray-100 pt-2 flex justify-between">
                  {po.excess_payment > 0 ? (
                    <>
                      <span className="text-gray-500">Excess Paid</span>
                      <span className="font-bold text-blue-600">{formatCurrency(po.excess_payment)}</span>
                    </>
                  ) : po.payment_balance > 0 ? (
                    <>
                      <span className="text-gray-500">Balance Due</span>
                      <span className="font-bold text-red-600 flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" />{formatCurrency(po.payment_balance)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-500">Status</span>
                      <span className="font-bold text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Fully Paid
                      </span>
                    </>
                  )}
                </div>
              </div>
              <Bar pct={po.paid_pct} color="purple" />
              <p className="text-[10px] text-gray-400 mt-1 text-right">{po.paid_pct}% paid</p>
            </div>
          </div>

          {/* Individual Sales list */}
          {po.sales && po.sales.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Sale Invoices ({po.sales.length})
              </p>
              <div className="space-y-2">
                {po.sales.map(sale => (
                  <div key={sale.id} className="bg-white border border-gray-200 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <span className="font-semibold text-sm text-gray-800">{sale.invoice_number}</span>
                        <span className="text-xs text-gray-400 ml-2">{safeDate(sale.sale_date)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        {sale.qty > 0 && <span className="text-gray-500">{sale.qty} pcs</span>}
                        <span className="font-bold text-gray-900">{formatCurrency(sale.total_amount)}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          sale.status === 'approved' ? 'bg-green-100 text-green-700' :
                          sale.status === 'draft'    ? 'bg-gray-100 text-gray-500'   :
                          'bg-amber-100 text-amber-700'
                        }`}>{sale.status}</span>
                      </div>
                    </div>
                    {/* Line items */}
                    {sale.items && sale.items.length > 0 && (
                      <div className="mt-2 border-t border-gray-100 pt-2 space-y-1">
                        {sale.items.map((item, i) => (
                          <div key={i} className="flex justify-between text-xs text-gray-500">
                            <span className="truncate flex-1">{item.item_name}</span>
                            <span className="ml-3 flex-shrink-0">{item.quantity} {item.unit} × {formatCurrency(item.rate)} = <strong className="text-gray-700">{formatCurrency(item.total)}</strong></span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {po.sale_count === 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-100 rounded-xl px-4 py-3">
              <AlertCircle className="w-4 h-4" /> No sales invoices linked to this PO yet
            </div>
          )}

          {po.project_name && (
            <div className="text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <span className="font-semibold text-amber-700">Linked Project: </span>{po.project_name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClientPOReportPage() {
  const { formatCurrency } = useSettings();
  const [pos, setPos]                   = useState([]);
  const [unlinkedSales, setUnlinkedSales] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId]     = useState(null);
  const [refreshing, setRefreshing]     = useState(false);
  const [showUnlinked, setShowUnlinked] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await api.get('/admin/reports/client-po');
      setPos(res.data.pos || []);
      setUnlinkedSales(res.data.unlinked_sales || []);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const clients = useMemo(() => [...new Set(pos.map(d => d.client_name))].sort(), [pos]);

  const filtered = useMemo(() => pos.filter(po => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      po.po_number.toLowerCase().includes(q) ||
      po.client_name.toLowerCase().includes(q) ||
      (po.item_name||'').toLowerCase().includes(q) ||
      (po.project_name||'').toLowerCase().includes(q) ||
      po.sales.some(s => s.invoice_number.toLowerCase().includes(q));
    const matchClient = !clientFilter || po.client_name === clientFilter;
    const matchStatus = !statusFilter || po.status === statusFilter;
    return matchSearch && matchClient && matchStatus;
  }), [pos, search, clientFilter, statusFilter]);

  const totals = useMemo(() => filtered.reduce((acc, po) => ({
    po_amount:       acc.po_amount       + po.po_amount,
    sale_amount:     acc.sale_amount     + po.sale_amount,
    remaining_amount: acc.remaining_amount + Math.max(0, po.remaining_amount),
    paid_amount:     acc.paid_amount     + po.paid_amount,
    payment_balance: acc.payment_balance + po.payment_balance,
    excess_payment:  acc.excess_payment  + po.excess_payment,
    advance_amount:  acc.advance_amount  + po.advance_amount,
  }), { po_amount:0, sale_amount:0, remaining_amount:0, paid_amount:0, payment_balance:0, excess_payment:0, advance_amount:0 }), [filtered]);

  if (loading) return (
    <div className="flex items-center justify-center h-60">
      <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" /> Client PO Report
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Purchase Order vs Sales Invoice vs Payment tracking</p>
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total PO Value',  value: totals.po_amount,       color: 'text-blue-600'   },
          { label: 'Total Invoiced',  value: totals.sale_amount,     color: 'text-amber-600'  },
          { label: 'Total Received',  value: totals.paid_amount,     color: 'text-green-600'  },
          { label: 'Balance Due',     value: totals.payment_balance, color: 'text-red-600'    },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-400 mb-1">{label}</p>
            <p className={`text-base font-bold ${color}`}>{formatCurrency(value)}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search PO number, client, item, invoice..."
            className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><X className="w-4 h-4" /></button>}
        </div>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white min-w-[160px]">
          <option value="">All Clients</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white min-w-[150px]">
          <option value="">All Status</option>
          {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <p className="text-xs text-gray-400">{filtered.length} PO{filtered.length !== 1 ? 's' : ''} · click to expand</p>

      {/* PO list */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400">No purchase orders found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(po => (
            <PORow key={po.id} po={po}
              expanded={expandedId === po.id}
              onToggle={() => setExpandedId(expandedId === po.id ? null : po.id)} />
          ))}
        </div>
      )}

      {/* Footer totals */}
      {filtered.length > 0 && (
        <div className="bg-slate-800 text-white rounded-2xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-3">Totals — {filtered.length} POs</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { l: 'PO Value',         v: totals.po_amount,        c: 'text-white'      },
              { l: 'Invoiced (Sale)',   v: totals.sale_amount,      c: 'text-amber-400'  },
              { l: 'Remaining to Bill',v: totals.remaining_amount,  c: 'text-orange-400' },
              { l: 'Payment Received', v: totals.paid_amount,       c: 'text-green-400'  },
              { l: 'Balance Due',      v: totals.payment_balance,   c: 'text-red-400'    },
              { l: 'Excess Paid',      v: totals.excess_payment,    c: 'text-blue-400'   },
              { l: 'Total Advance',    v: totals.advance_amount,    c: 'text-purple-400' },
            ].map(({ l, v, c }) => (
              <div key={l}>
                <p className="text-xs text-slate-400">{l}</p>
                <p className={`text-sm font-bold ${c}`}>{formatCurrency(v)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unlinked Sales section */}
      {unlinkedSales.length > 0 && (
        <div className="border border-orange-200 bg-orange-50 rounded-2xl p-4">
          <button onClick={() => setShowUnlinked(u => !u)}
            className="w-full flex items-center justify-between gap-2 text-sm font-semibold text-orange-700">
            <span className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {unlinkedSales.length} Sale Invoice{unlinkedSales.length !== 1 ? 's' : ''} not linked to any PO
            </span>
            {showUnlinked ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showUnlinked && (
            <div className="mt-3 space-y-2">
              {unlinkedSales.map(s => (
                <div key={s.id} className="bg-white rounded-xl border border-orange-100 px-3 py-2 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-semibold text-gray-800">{s.invoice_number}</span>
                    <span className="text-gray-400 ml-2 text-xs">{safeDate(s.sale_date)}</span>
                    <span className="text-gray-500 ml-2 text-xs">· {s.client_name}</span>
                  </div>
                  <span className="font-bold text-gray-900">{formatCurrency(s.total_amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
